import { useState, useEffect, useRef, memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const TestimonialsSection = memo(() => {
  const headerReveal = useScrollAnimation({ threshold: 0.1, rootMargin: '-10% 0px' });
  const carouselReveal = useScrollAnimation({ threshold: 0.1, rootMargin: '-10% 0px' });
  const [api, setApi] = useState<CarouselApi>();
  const [isHovered, setIsHovered] = useState(false);
  
  const results = [
    {
      id: 1,
      image: "/lovable-uploads/quantmake-depoimento-cliente-01-resultado-505-reais-day-trade.png",
      profit: "R$\u00A0505,00",
      operations: "11",
      winRate: "100,00%"
    },
    {
      id: 2,
      image: "/lovable-uploads/quantmake-depoimento-cliente-02-resultado-1010-reais-robo-trader.png",
      profit: "R$\u00A01.010,00",
      operations: "1",
      winRate: "100,00%"
    },
    {
      id: 3,
      image: "/lovable-uploads/quantmake-depoimento-cliente-03-resultado-1290-reais-copy-trade.png",
      profit: "R$\u00A01.290,00",
      operations: "3",
      winRate: "100,00%"
    },
    {
      id: 4,
      image: "/lovable-uploads/quantmake-depoimento-cliente-04-resultado-124-reais-trading-automatizado.png",
      profit: "R$\u00A0124,00",
      operations: "17",
      winRate: "47,06%"
    },
    {
      id: 5,
      image: "/lovable-uploads/quantmake-depoimento-cliente-05-resultado-1237-reais-quanttrade.png",
      profit: "R$\u00A01.237,00",
      operations: "11",
      winRate: "100,00%"
    },
    {
      id: 6,
      image: "/lovable-uploads/quantmake-depoimento-cliente-06-resultado-270-reais-autotrade.png",
      profit: "R$\u00A0270,00",
      operations: "12",
      winRate: "100,00%"
    },
    {
      id: 7,
      image: "/lovable-uploads/quantmake-depoimento-cliente-07-resultado-610-reais-nelogica-profit.png",
      profit: "R$\u00A0610,20",
      operations: "14",
      winRate: "100,00%"
    },
    {
      id: 8,
      image: "/lovable-uploads/quantmake-depoimento-cliente-08-resultado-296-reais-blackarrow.png",
      profit: "R$\u00A0296,00",
      operations: "3",
      winRate: "100,00%"
    },
    {
      id: 9,
      image: "/lovable-uploads/quantmake-depoimento-cliente-09-resultado-678-reais-trading-quantitativo.png",
      profit: "R$\u00A0678,50",
      operations: "8",
      winRate: "75,00%"
    },
    {
      id: 10,
      image: "/lovable-uploads/quantmake-depoimento-cliente-10-resultado-358-reais-algoritmo-quantitativo.png",
      profit: "R$\u00A0358,00",
      operations: "19",
      winRate: "89,47%"
    },
    {
      id: 11,
      image: "/lovable-uploads/quantmake-depoimento-cliente-11-resultado-370-reais-ia-trading.png",
      profit: "R$\u00A0370,00",
      operations: "7",
      winRate: "57,14%"
    },
    {
      id: 12,
      image: "/lovable-uploads/quantmake-depoimento-cliente-12-resultado-310-reais-sistema-automatizado.png",
      profit: "R$\u00A0310,00",
      operations: "3",
      winRate: "66,67%"
    },
    {
      id: 13,
      image: "/lovable-uploads/quantmake-depoimento-cliente-13-resultado-125-reais-trading-inteligente.png",
      profit: "R$\u00A0125,00",
      operations: "1",
      winRate: "100,00%"
    },
    {
      id: 14,
      image: "/lovable-uploads/quantmake-depoimento-cliente-14-resultado-6891-reais-robos-traders.png",
      profit: "R$\u00A06.891,00",
      operations: "31",
      winRate: "90,32%"
    },
    {
      id: 15,
      image: "/lovable-uploads/quantmake-depoimento-cliente-15-resultado-193-reais-estrategia-quantitativa.png",
      profit: "R$\u00A0193,00",
      operations: "1",
      winRate: "100,00%"
    },
    {
      id: 16,
      image: "/lovable-uploads/quantmake-depoimento-cliente-16-resultado-335-reais-fibonacci-trader.png",
      profit: "R$\u00A0335,00",
      operations: "2",
      winRate: "50,00%"
    },
    {
      id: 17,
      image: "/lovable-uploads/quantmake-depoimento-cliente-17-resultado-340-reais-smartgrid-premium.png",
      profit: "R$\u00A0340,00",
      operations: "11",
      winRate: "100,00%"
    },
    {
      id: 18,
      image: "/lovable-uploads/quantmake-depoimento-cliente-18-resultado-312-reais-larry-williams.png",
      profit: "R$\u00A0312,00",
      operations: "2",
      winRate: "100,00%"
    },
    {
      id: 19,
      image: "/lovable-uploads/quantmake-depoimento-cliente-19-resultado-237-reais-combo-ultra.png",
      profit: "R$\u00A0237,00",
      operations: "5",
      winRate: "100,00%"
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    if (!api) return;

    const autoScroll = () => {
      if (!isHovered) {
        api.scrollNext();
      }
    };

    const interval = setInterval(autoScroll, 4000);
    return () => clearInterval(interval);
  }, [api, isHovered]);

  return (
    <section id="testimonials" className="py-20 bg-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-12 transition-all duration-500 will-change-transform ${
            headerReveal.isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-100 translate-y-2'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Resultados Reais dos nossos <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">Assinantes</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Contas reais de clientes utilizando nossos algoritmos quantitativos. Resultados comprovados em ambiente de produção.
          </p>
        </div>

        <div 
          ref={carouselReveal.ref}
          className={`relative px-4 md:px-16 lg:px-20 transition-all duration-500 will-change-transform ${
            carouselReveal.isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-100 translate-y-2'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
            setApi={setApi}
          >
            <CarouselContent className="gap-4 md:gap-6 ml-0 md:-ml-4">
              {results.map((result) => (
                <CarouselItem key={result.id} className="basis-[85%] md:basis-[48%] lg:basis-[32%] pl-4 md:pl-4">
                  <Card className="bg-slate-700/50 border-slate-600 hover:bg-slate-700/70 transition-all duration-300 h-[420px] md:h-full overflow-hidden">
                    <CardContent className="p-0">
                      {/* Trading Platform Screenshot */}
                      <div className="relative">
                        <img 
                          src={result.image} 
                          alt={`Resultado real trading automatizado QuantMake: lucro ${result.profit} em ${result.operations} com taxa de acerto ${result.winRate} - robô trader day trade`}
                          className="w-full h-80 object-cover object-left-top"
                          title={`Performance do robô trader: ${result.profit} de lucro verificado`}
                          loading={result.id <= 3 ? "eager" : "lazy"}
                          decoding="async"
                          onError={(e) => {
                            console.warn(`Failed to load image: ${result.image}`);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                      </div>

                       {/* Results Summary */}
                      <div className="p-4 md:p-6 bg-gradient-to-br from-slate-800/80 to-slate-900/80">
                        <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
                          <div>
                            <div className="text-base md:text-lg font-bold text-green-400">{result.profit}</div>
                            <div className="text-xs md:text-sm text-slate-400">Lucro</div>
                          </div>
                          <div>
                            <div className="text-base md:text-lg font-bold text-cyan-400">{result.operations}</div>
                            <div className="text-xs md:text-sm text-slate-400">Operações</div>
                          </div>
                          <div>
                            <div className="text-base md:text-lg font-bold text-yellow-400">{result.winRate}</div>
                            <div className="text-xs md:text-sm text-slate-400">Acertos</div>
                          </div>
                        </div>
                        
                        <div className="mt-4 text-center">
                          <span className="inline-block bg-green-400/20 text-green-400 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-semibold">
                            ✅ Conta Real Verificada
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Navigation Buttons - Same style as RobotsSection */}
            <button
              onClick={() => api?.scrollPrev()}
              className="group hidden md:flex absolute -left-4 sm:-left-6 top-1/2 -translate-y-1/2 bg-blue-400/40 backdrop-blur-md border-2 border-blue-400/60 text-blue-100 hover:bg-green-400/40 hover:border-green-400/60 hover:text-white shadow-lg shadow-blue-400/40 hover:shadow-lg hover:shadow-green-400/40 h-10 w-10 sm:h-12 sm:w-12 rounded-full items-center justify-center transition-all duration-300 z-20"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:-translate-x-1" />
            </button>
            <button
              onClick={() => api?.scrollNext()}
              className="group hidden md:flex absolute -right-4 sm:-right-6 top-1/2 -translate-y-1/2 bg-blue-400/40 backdrop-blur-md border-2 border-blue-400/60 text-blue-100 hover:bg-green-400/40 hover:border-green-400/60 hover:text-white shadow-lg shadow-blue-400/40 hover:shadow-lg hover:shadow-green-400/40 h-10 w-10 sm:h-12 sm:w-12 rounded-full items-center justify-center transition-all duration-300 z-20"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </Carousel>
        </div>

        {/* Additional Trust Indicators */}
        <div className="mt-8 sm:mt-12 text-center px-4">
          <div className="inline-flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-6 bg-slate-700/30 rounded-xl sm:rounded-2xl px-4 py-4 sm:px-8 sm:py-4 max-w-4xl mx-auto">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full flex-shrink-0"></div>
              <span className="text-slate-300 text-sm sm:text-sm font-medium">Resultados em Tempo Real</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-slate-600"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-cyan-400 rounded-full flex-shrink-0"></div>
              <span className="text-slate-300 text-sm sm:text-sm font-medium">Contas Verificadas</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-slate-600"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full flex-shrink-0"></div>
              <span className="text-slate-300 text-sm sm:text-sm font-medium">Ambiente de Produção</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

TestimonialsSection.displayName = 'TestimonialsSection';

export default TestimonialsSection;